// src/components/theme/CustomColorPicker.js

import React, { useState, useEffect } from 'react';
import { SketchPicker } from 'react-color'; // Ensure 'react-color' is installed
import PropTypes from 'prop-types';

const CustomColorPicker = ({ label, color, onChange }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [inputValue, setInputValue] = useState(color ? color.replace('#', '') : 'FFFFFF');
  const [isEyeDropperSupported, setIsEyeDropperSupported] = useState(false);

  // Check if the EyeDropper API is supported
  useEffect(() => {
    if (window.EyeDropper) {
      setIsEyeDropperSupported(true);
    }
  }, []);

  // Update inputValue when color prop changes
  useEffect(() => {
    setInputValue(color ? color.replace('#', '') : 'FFFFFF');
  }, [color]);

  const handleClick = () => {
    setDisplayColorPicker(!displayColorPicker);
  };

  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  const handleChange = (newColor) => {
    setInputValue(newColor.hex.replace('#', ''));
    onChange(newColor.hex);
  };

  const handleInputChange = (e) => {
    let newColor = e.target.value.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
    setInputValue(newColor);
    if (/^[0-9A-F]{6}$/.test(newColor)) {
      onChange('#' + newColor);
    }
  };

  const handleEyeDropper = async () => {
    if (!isEyeDropperSupported) {
      alert('EyeDropper API is not supported in this browser.');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      if (result.sRGBHex) {
        setInputValue(result.sRGBHex.replace('#', ''));
        onChange(result.sRGBHex);
      }
    } catch (error) {
      console.error('EyeDropper cancelled or failed:', error);
    }
  };

  return (
    <div style={styles.container}>
      <label>{label}:</label>
      <div style={styles.inputContainer}>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          style={styles.input}
          maxLength={6}
          placeholder="FFFFFF"
          aria-label={`Enter ${label}`}
        />
        <div
          style={{
            ...styles.colorSquare,
            backgroundColor: `#${inputValue}`,
          }}
        ></div>
        <button
          className='btn btn-primary'
          onClick={handleClick}
          style={styles.button}
          aria-label={`Pick ${label}`}
        >
          Pick
        </button>
        {isEyeDropperSupported && (
          <button
            className="btn btn-primary"
            onClick={handleEyeDropper}
            style={{ ...styles.button, marginLeft: '10px' }}
            aria-label={`Use Eye Dropper for ${label}`}
            title="Use Eye Dropper"
          >
            <i className="fa fa-eye-dropper" aria-hidden="true"></i>
          </button>
        )}
      </div>
      {displayColorPicker ? (
        <div style={styles.popover}>
          <div style={styles.cover} onClick={handleClose} />
          <SketchPicker color={`#${inputValue}`} onChange={handleChange} />
        </div>
      ) : null}
    </div>
  );
};

CustomColorPicker.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const styles = {
  container: {
    marginBottom: '10px',
    position: 'relative',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px',
  },
  input: {
    width: '80px',
    padding: '5px',
    marginRight: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    textTransform: 'uppercase',
  },
  colorSquare: {
    width: '36px',
    height: '36px',
    border: '1px solid #777',
    borderRadius: '5px',
    marginRight: '10px',
  },
  button: {
    padding: '5px 10px',
    cursor: 'pointer',
  },
  popover: {
    position: 'absolute',
    zIndex: '2',
    top: '60px', // Adjust based on your layout
  },
  cover: {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
  },
  warning: {
    marginTop: '5px',
  },
};

export default CustomColorPicker;
